import React, { useContext } from 'react';
import styled from 'styled-components';
import { Trans } from '@lingui/react';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { Container } from '@components/layout/Grid';
import Icon from '@components/Icon';
import {
  CurrentUser,
  GenericProgram,
  PriceTypeKind,
  ProgramAccessStatus,
  ProgramCategory,
} from '@gql/generated';
import LanguageContext from '@lib/contexts/languageContext';
import { Button } from '@components/ButtonV2';
import { getButtonAppearance } from '@templates/pages/program/overview/WeeklyProgramOverview';

const Banner = styled.div<{ appearance?: 'info' | 'error' }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  border: 1px solid
    ${({ appearance, theme }) =>
      appearance === 'error'
        ? theme.colors.secondary['red-1']
        : theme.colors.secondary['orange-1']};
  padding: 16px 24px 16px 16px;
  background: none;

  i:before {
    color: ${({ appearance, theme }) =>
      appearance === 'error'
        ? theme.colors.secondary['red-1']
        : theme.colors.secondary['orange-1']};
  }

  a {
    width: 100%;
    flex-shrink: 0;
    margin-left: auto;
    margin-right: 0;
  }

  i:before {
    font-size: 20px;
  }

  @media (min-width: ${({ theme }) => theme.viewports.tablet}px) {
    flex-direction: row;
    align-items: center;

    a {
      width: auto;
    }
  }
`;

const Text = styled.p`
  flex: 1;
  max-width: 860px;
  font-size: 13px;
  line-height: 23px;
  margin-right: 20px;
`;

function getTexts({
  price,
  accessStatus,
  accessUntil,
  discountValue,
  discount,
  programCategory,
  currentUser,
  priceType,
}) {
  let text, buttonText;
  if (accessStatus === ProgramAccessStatus['ExpiringSoon']) {
    const days = differenceInCalendarDays(new Date(accessUntil), new Date());

    if (discountValue) {
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expires_soon.extend_with_discount_text"
            values={{ days, discount }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expires_soon.extend_with_discount_text"
            values={{ days, discount }}
            components={[<a href="#" key="0"></a>]}
          />
        );
      buttonText = (
        <Trans
          id="programs_2021.expiration_banner.extend_button"
          values={{
            price: `${(price?.discountedPrice / 100).toFixed(2)} ${
              price?.currency
            }`,
          }}
        />
      );
    } else if (
      priceType === PriceTypeKind['FreeForAll'] ||
      ([
        PriceTypeKind['BySubscription'],
        PriceTypeKind['FreeForPaying'],
      ].includes(priceType) &&
        currentUser.isNoneTrialPremium)
    ) {
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expire_soon.renew_access_free_text"
            values={{ days }}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expire_soon.renew_access_free_text"
            values={{ days }}
          />
        );
      buttonText = (
        <Trans id="programs_2021.expiration_banner.expire_soon.renew_free_button" />
      );
    } else if (price?.originalPrice && !price?.discountApplied) {
      // buy this program at this price
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expire_soon.buy_program_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expire_soon.buy_program_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        );
      buttonText = (
        <Trans
          id="programs_2021.expiration_banner.extend_button"
          values={{
            price: `${(price.originalPrice / 100).toFixed(2)} ${
              price.currency
            }`,
          }}
        />
      );
    } else {
      // upgrade to join
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expire_soon.upgrade_to_renew_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expire_soon.upgrade_to_renew_text"
            values={{ days }}
          />
        );

      buttonText = (
        <Trans id="programs_2021.expiration_banner.expire_soon.upgrade_button" />
      );
    }
  }
  // Expired
  else if (accessStatus === ProgramAccessStatus['Expired']) {
    const days = differenceInCalendarDays(new Date(), new Date(accessUntil));
    if (discountValue) {
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expired.renew_with_discount_text"
            values={{ days, discount }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expired.renew_with_discount_text"
            values={{ days, discount }}
            components={[<a href="#" key="0"></a>]}
          />
        );
      buttonText = (
        <Trans
          id="programs_2021.expiration_banner.extend_button"
          values={{
            price: `${(price?.discountedPrice / 100).toFixed(2)} ${
              price?.currency
            }`,
          }}
        />
      );
    } else if (
      priceType === PriceTypeKind['FreeForAll'] ||
      ([
        PriceTypeKind['BySubscription'],
        PriceTypeKind['FreeForPaying'],
      ].includes(priceType) &&
        currentUser?.isNoneTrialPremium)
    ) {
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expired.renew_access_free_text"
            values={{ days }}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expired.renew_access_free_text"
            values={{ days }}
          />
        );
      buttonText = (
        <Trans id="programs_2021.expiration_banner.expired.renew_free_button" />
      );
    } else if (price?.originalPrice && !price?.discountApplied) {
      // buy this program at price
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expired.buy_program_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expired.buy_program_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        );
      buttonText = (
        <Trans
          id="programs_2021.expiration_banner.extend_button"
          values={{
            price: `${(price.originalPrice / 100).toFixed(2)} ${
              price.currency
            }`,
          }}
        />
      );
    } else {
      // upgrade to join
      text =
        programCategory === ProgramCategory['Courses'] ? (
          <Trans
            id="courses_2021.expiration_banner.expired.upgrade_to_renew_text"
            values={{ days }}
            components={[<a href="#" key="0"></a>]}
          />
        ) : (
          <Trans
            id="programs_2021.expiration_banner.expired.upgrade_to_renew_text"
            values={{ days }}
          />
        );

      buttonText = (
        <Trans id="programs_2021.expiration_banner.expire_soon.upgrade_button" />
      );
    }
  }

  return [text, buttonText];
}

type Props = {
  currentUser: CurrentUser;
  programCategory: ProgramCategory;
  accessUntil: Date;
  price: GenericProgram['price'];
  priceType: GenericProgram['priceType'];
  accessStatus: GenericProgram['accessStatus'];
  programSlug: string;
};
const ProgramExpirationBanner = (props: Props) => {
  const {
    price,
    accessStatus,
    accessUntil,
    programSlug,
    priceType,
    programCategory,
    currentUser,
  } = props;
  const lang = useContext(LanguageContext);

  if (priceType === PriceTypeKind['AccessCodesOnly'] || !currentUser?.id) {
    return null;
  }

  const discountValue =
    price?.originalPrice && price?.discountedPrice
      ? Math.round(
          ((price.originalPrice - price.discountedPrice) * 100) /
            price.originalPrice
        )
      : null;

  const discount = discountValue + '%';

  let text, buttonText;
  if (
    [
      ProgramAccessStatus['ExpiringSoon'],
      ProgramAccessStatus['Expired'],
    ].includes(accessStatus)
  ) {
    //
    const [_text, _buttonText] = getTexts({
      price,
      accessStatus,
      accessUntil,
      discountValue,
      discount,
      programCategory,
      currentUser,
      priceType,
    });

    text = _text;
    buttonText = _buttonText;
  } else {
    return null;
  }

  return (
    <Container px={20} my={20} data-testid="ProgramExpirationBanner">
      <Banner
        appearance={
          accessStatus === ProgramAccessStatus['Expired'] ? 'error' : 'info'
        }
      >
        <Icon name="ygb-icon-infob" mr="10px" />
        <Text data-testid="ProgramExpirationBanner--Text">{text}</Text>
        <Button
          href={`/${lang}/${programCategory}/${programSlug}/get-started`}
          appearance={getButtonAppearance(programCategory)}
          data-testid="ProgramExpirationBanner--Button"
        >
          {buttonText}
        </Button>
      </Banner>
    </Container>
  );
};

export default ProgramExpirationBanner;
